import { useMutation } from '@tanstack/react-query';
import type { CrmComposeMessageBody, CrmManagerHintDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@upme/ui/components/dialog';
import { Label } from '@upme/ui/components/label';
import { cn } from '@upme/ui/lib/utils';
import { Copy } from 'lucide-react';
import { type ChangeEvent, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { composeCrmHintMessage } from '../mutations';

type Props = {
  hint: CrmManagerHintDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CrmHintComposeDialog({ hint, open, onOpenChange }: Props) {
  const [draft, setDraft] = useState('');
  const [contextText, setContextText] = useState('');

  const template = useMutation({
    mutationFn: (body: CrmComposeMessageBody) => composeCrmHintMessage(body),
    onSuccess: (data) => setDraft(data.draft),
    onError: () => toast.error('Не удалось собрать черновик'),
  });

  const refine = useMutation({
    mutationFn: (body: CrmComposeMessageBody) => composeCrmHintMessage(body),
    onSuccess: (data) => {
      setDraft(data.draft);
      if (data.usedAi) toast.success('Черновик уточнён с ИИ');
    },
    onError: () => toast.error('ИИ не ответил — оставлен прежний текст'),
  });

  useEffect(() => {
    if (!open || !hint) return;
    setContextText('');
    setDraft('');
    template.mutate({
      hintId: hint.hintId,
      kind: hint.kind,
      contactUserId: hint.contactUserId,
      ...(hint.event?.id ? { eventId: hint.event.id } : {}),
      useAi: false,
    });
  }, [open, hint?.hintId]);

  function onOpenChangeInternal(next: boolean) {
    onOpenChange(next);
    if (!next) {
      setDraft('');
      setContextText('');
    }
  }

  async function copyDraft() {
    if (!draft.trim()) return;
    try {
      await navigator.clipboard.writeText(draft);
      toast.success('Скопировано');
    } catch {
      toast.error('Не удалось скопировать');
    }
  }

  function onRefineAi() {
    if (!hint) return;
    refine.mutate({
      hintId: hint.hintId,
      kind: hint.kind,
      contactUserId: hint.contactUserId,
      ...(hint.event?.id ? { eventId: hint.event.id } : {}),
      contextText: contextText.trim(),
      useAi: true,
    });
  }

  const busy = template.isPending || refine.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChangeInternal}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Сообщение для {hint?.contactLabel ?? 'контакта'}</DialogTitle>
          <DialogDescription>
            Шаблон готов сразу. Добавьте контекст и нажмите «Уточнить с ИИ», если нужна более живая
            формулировка.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="crm-hint-draft">Черновик</Label>
            <textarea
              id="crm-hint-draft"
              value={draft}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
              rows={8}
              className={cn(
                'flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              )}
              disabled={busy && !draft}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="crm-hint-context">Контекст для ИИ (необязательно)</Label>
            <textarea
              id="crm-hint-context"
              value={contextText}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setContextText(e.target.value)}
              rows={3}
              placeholder="Например: видели её в чате про йогу, хочется мягко пригласить"
              className={cn(
                'flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              )}
            />
          </div>
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button type="button" variant="outline" onClick={() => void copyDraft()} disabled={!draft.trim()}>
            <Copy className="mr-1.5 size-4" aria-hidden />
            Копировать
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onRefineAi}
              disabled={busy || !contextText.trim()}
            >
              Уточнить с ИИ
            </Button>
            <Button type="button" onClick={() => onOpenChangeInternal(false)}>
              Готово
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
